import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import * as OBC from "@thatopen/components";
import { ViewportGizmo } from "three-viewport-gizmo";
import { DebugOverlay, type FragDebugInfo } from "./DebugOverlay";
import { LoadStatusOverlay } from "./LoadStatusOverlay";

import type { FragBimViewerProps, FragViewerLoadStatus, LoadedModel } from "./types";
import {
  applyColorRulesToModels,
  applyVisibilityFilterToModels,
} from "./visibilityFilter";
import {
  debugLog,
  FLOOR_CLIP_BOTTOM_TOLERANCE_M,
  FLOOR_CLIP_TOP_TOLERANCE_M,
  getModelInputId,
  getRetainedModelIds,
  yieldToBrowser,
} from "./viewerUtils";

export type {
  FragBimViewerProps,
  FragColorRule,
  FragFilter,
  FragManifestEntry,
  FragModelInput,
  IFloorClip,
  ISyncCamera,
} from "./types";

// --- Small inline icon components for the selection toolbar ---
const selIconStyle: React.CSSProperties = { display: "block", width: 14, height: 14 };

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" style={selIconStyle} aria-hidden="true">
    <path d="m3 3 18 18M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-4.8M7.3 7.5C4.3 9.2 2.5 12 2.5 12s3.5 6 9.5 6c1.7 0 3.2-.5 4.5-1.2M19.1 14.1c1.5-1.1 2.4-2.1 2.4-2.1s-3.5-6-9.5-6c-.8 0-1.6.1-2.3.3"
      fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" style={selIconStyle} aria-hidden="true">
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
      fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const IsolateIcon = () => (
  <svg viewBox="0 0 24 24" style={selIconStyle} aria-hidden="true">
    <path d="M3 12h3M18 12h3M12 3v3M12 18v3"
      stroke="currentColor" strokeLinecap="round" strokeWidth="2" fill="none" />
    <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

/** Internal context stored for the lifetime of the viewer. */
type OBCContext = {
  components: OBC.Components;
  world: OBC.World;
  fragments: OBC.FragmentsManager;
  clipper: OBC.Clipper;
  grid: OBC.SimpleGrid;
  loadedModels: Map<string, LoadedModel>;
  globalClipBottom: THREE.Plane;
  globalClipTop: THREE.Plane;
};

const FragBimViewer: React.FC<FragBimViewerProps> = ({
  models,
  filter,
  colorRules,
  syncCamera,
  onCameraChange,
  onLoadedModelIdsChange,
  debugOverlay = false,
  floorClip,
  clipperActive = false,
  onClipperActiveChange,
  deleteAllClipPlanesSignal,
  showOrbitGizmo = true,
  gridVisible = false,
  onObjectClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const gizmoContainerRef = useRef<HTMLDivElement>(null);
  const ctxRef = useRef<OBCContext | null>(null);
  const gizmoRef = useRef<ViewportGizmo | null>(null);
  const gizmoRendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const needsRenderRef = useRef(true);
  const hasFitCameraRef = useRef(false);
  const modelCenter3DRef = useRef<THREE.Vector3 | null>(null);
  const loadingModelIdsRef = useRef<Set<string>>(new Set());
  const desiredModelIdsRef = useRef<Set<string>>(new Set());
  const lastSyncKeyRef = useRef<string>("");
  const lastBboxRef = useRef<THREE.Box3 | null>(null);
  const lastLoadStatusUpdateRef = useRef(0);
  const filterRef = useRef(filter);
  const [sceneReady, setSceneReady] = useState(false);
  const [loadedModelVersion, setLoadedModelVersion] = useState(0);
  const [loadStatus, setLoadStatus] = useState<FragViewerLoadStatus | null>({
    title: "Starter 3D-visning",
    detail: "Initialiserer rendrer og 3D-worker...",
    blocking: true,
  });
  const [hasSelection, setHasSelection] = useState(false);
  const [hasHiddenObjects, setHasHiddenObjects] = useState(false);
  const selectionRef = useRef<Map<any, Set<number>>>(new Map());
  const modelsKey = useMemo(
    () => models?.map((model) => getModelInputId(model)).join("|") ?? "",
    [models],
  );

  const setLoadStatusThrottled = (status: FragViewerLoadStatus | null, force = false) => {
    const now = performance.now();
    if (!force && now - lastLoadStatusUpdateRef.current < 120) return;
    lastLoadStatusUpdateRef.current = now;
    setLoadStatus(status);
  };

  // Refs so callbacks created once always read latest prop values
  const syncCameraRef = useRef(syncCamera);
  const onCameraChangeRef = useRef(onCameraChange);
  const onLoadedModelIdsChangeRef = useRef(onLoadedModelIdsChange);
  const onObjectClickRef = useRef(onObjectClick);
  const debugOverlayRef = useRef(debugOverlay);
  useEffect(() => { filterRef.current = filter; }, [filter]);
  useEffect(() => { syncCameraRef.current = syncCamera; }, [syncCamera]);
  useEffect(() => { onCameraChangeRef.current = onCameraChange; }, [onCameraChange]);
  useEffect(() => { onLoadedModelIdsChangeRef.current = onLoadedModelIdsChange; }, [onLoadedModelIdsChange]);
  useEffect(() => { onObjectClickRef.current = onObjectClick; }, [onObjectClick]);
  useEffect(() => { debugOverlayRef.current = debugOverlay; }, [debugOverlay]);

  const notifyRetainedModelIds = (loadedModels: Map<string, LoadedModel>) => {
    onLoadedModelIdsChangeRef.current?.(
      getRetainedModelIds(loadedModels.keys(), loadingModelIdsRef.current),
    );
  };

  // --- Floor clipping ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const renderer = (ctx.world.renderer as OBC.SimpleRenderer).three;

    if (floorClip) {
      ctx.globalClipBottom.normal.set(0, 1, 0);
      ctx.globalClipBottom.constant = -(floorClip.bottom - FLOOR_CLIP_BOTTOM_TOLERANCE_M);
      ctx.globalClipTop.normal.set(0, -1, 0);
      ctx.globalClipTop.constant = floorClip.top - FLOOR_CLIP_TOP_TOLERANCE_M;

      // Ensure floor planes are in the clipping array
      const others = renderer.clippingPlanes.filter(
        (p) => p !== ctx.globalClipBottom && p !== ctx.globalClipTop,
      );
      renderer.clippingPlanes = [ctx.globalClipBottom, ctx.globalClipTop, ...others];
    } else {
      renderer.clippingPlanes = renderer.clippingPlanes.filter(
        (p) => p !== ctx.globalClipBottom && p !== ctx.globalClipTop,
      );
    }
    renderer.localClippingEnabled = renderer.clippingPlanes.length > 0;

    // Force fragment LOD system to re-evaluate with new clipping planes
    ctx.fragments.core.update(true);

    needsRenderRef.current = true;
  }, [floorClip, sceneReady, loadedModelVersion]);

  // Debug overlay state
  const [debugInfo, setDebugInfo] = React.useState<FragDebugInfo | null>(null);

  // --- Scene setup (once on mount) ---
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    setLoadStatus({ title: "Starter 3D-visning", detail: "Henter 3D-worker...", blocking: true });

    // --- OBC Components init ---
    const components = new OBC.Components();
    const worlds = components.get(OBC.Worlds);
    const world = worlds.create<OBC.SimpleScene, OBC.OrthoPerspectiveCamera, OBC.SimpleRenderer>();

    world.scene = new OBC.SimpleScene(components);
    world.scene.setup();
    world.scene.three.background = new THREE.Color(0x1a1a2e);

    world.renderer = new OBC.SimpleRenderer(components, container, {
      antialias: false,
      powerPreference: "high-performance",
    });
    world.renderer.showLogo = false;

    world.camera = new OBC.OrthoPerspectiveCamera(components);
    const camera = world.camera.three as THREE.PerspectiveCamera;
    camera.near = 0.5;
    camera.far = 10000;
    camera.updateProjectionMatrix();

    // OBC OrthoPerspectiveCamera (Orbit mode) defaults to minDistance=1,
    // maxDistance=300. camera-controls scales wheel zoom by distance-to-
    // target, so when the orbit pivot is close to the camera, zoom feels
    // stuck. Loosen the clamps and bump dollySpeed.
    world.camera.controls.minDistance = 0.05;
    world.camera.controls.maxDistance = 400;
    world.camera.controls.dollySpeed = 2;

    components.init();

    // Setup raycaster (required by clipper)
    const casters = components.get(OBC.Raycasters);
    casters.get(world);

    // Setup clipper
    const clipper = components.get(OBC.Clipper);
    clipper.enabled = false;

    // Setup fragments
    const fragments = components.get(OBC.FragmentsManager);

    (async () => {
      const workerUrl = await OBC.FragmentsManager.getWorker();
      if (cancelled) return;
      fragments.init(workerUrl);

      // Wire up camera-driven LOD updates
      world.camera.controls.addEventListener("update", () => {
        fragments.core.update();
        needsRenderRef.current = true;
      });

      // Rebind models to active camera (required by Raycasters example)
      world.onCameraChanged.add((cam) => {
        for (const [, model] of fragments.list) {
          model.useCamera((cam as OBC.OrthoPerspectiveCamera).three);
        }
        fragments.core.update(true);
      });

      // When models are added to the fragment list, wire them up
      fragments.list.onItemSet.add(({ value: model }) => {
        model.useCamera(camera);
        world.scene.three.add(model.object);
        model.getClippingPlanesEvent = () => {
          return Array.from(world.renderer!.three.clippingPlanes) || [];
        };
        fragments.core.update(true);
        needsRenderRef.current = true;
      });

      // Remove z-fighting on fragment materials
      fragments.core.models.materials.list.onItemSet.add(({ value: material }) => {
        if (!("isLodMaterial" in material && material.isLodMaterial)) {
          material.polygonOffset = true;
          material.polygonOffsetUnits = 1;
          material.polygonOffsetFactor = Math.random();
        }
      });

      // Create grid
      const grids = components.get(OBC.Grids);
      const grid = grids.create(world);
      grid.material.uniforms.uColor.value = new THREE.Color(0xcccccc);
      grid.visible = false;

      ctxRef.current = {
        components,
        world,
        fragments,
        clipper,
        grid,
        loadedModels: new Map(),
        globalClipBottom: new THREE.Plane(new THREE.Vector3(0, 1, 0), 0),
        globalClipTop: new THREE.Plane(new THREE.Vector3(0, -1, 0), 0),
      };

      // Create orbit gizmo — cube style, BIM axis labels.
      // Use a dedicated WebGLRenderer + canvas for the gizmo so OBC's main
      // renderer (which can resize, clear, scissor, etc.) cannot overwrite the
      // gizmo output. The gizmo's canvas is positioned absolutely on top of the
      // viewer.
      const gizmoContainer = gizmoContainerRef.current!;
      const gizmoRenderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
      });
      gizmoRenderer.setPixelRatio(window.devicePixelRatio);
      gizmoRenderer.setSize(120, 120);
      gizmoRenderer.setClearColor(0x000000, 0);
      gizmoRenderer.domElement.style.width = "100%";
      gizmoRenderer.domElement.style.height = "100%";
      gizmoRenderer.domElement.style.display = "block";
      gizmoContainer.appendChild(gizmoRenderer.domElement);
      gizmoRendererRef.current = gizmoRenderer;

      const gizmo = new ViewportGizmo(camera, gizmoRenderer, {
        type: "cube",
        container: gizmoContainer,
        placement: "top-right",
        size: 120,
        animated: true,
        speed: 2,
        x: { label: "Right" },
        nx: { label: "Left" },
        y: { label: "Top" },
        ny: { label: "Bottom" },
        z: { label: "Front" },
        nz: { label: "Back" },
      });
      const obcControls = (world.camera as OBC.OrthoPerspectiveCamera).controls;
      gizmo.target = obcControls.getTarget(new THREE.Vector3());
      gizmo.addEventListener("start", () => {
        obcControls.enabled = false;
      });
      gizmo.addEventListener("end", () => {
        obcControls.enabled = true;
        needsRenderRef.current = true;
      });
      gizmo.addEventListener("change", () => {
        // Sync camera-controls to match the gizmo's camera position & target
        const pos = camera.position;
        const t = gizmo.target;
        obcControls.setLookAt(pos.x, pos.y, pos.z, t.x, t.y, t.z, false);
        needsRenderRef.current = true;
      });
      gizmoRef.current = gizmo;

      // Keep gizmo viewport in sync when the renderer canvas resizes
      // (OBC SimpleRenderer resizes its canvas to match its host container).
      const canvasEl = world.renderer!.three.domElement;
      const resizeObserver = new ResizeObserver(() => {
        gizmoRef.current?.update(false);
      });
      resizeObserver.observe(canvasEl);
      resizeObserver.observe(gizmoContainer);
      resizeObserverRef.current = resizeObserver;

      // Render gizmo immediately after OBC's scene render (same call stack, no race)
      world.renderer!.onAfterUpdate.add(() => {
        const g = gizmoRef.current;
        if (g?.enabled) {
          g.update(false);
          g.render();
        }
      });

      // Double-press scroll wheel (middle button) → fit camera to model
      let lastMiddleClickTime = 0;
      const DOUBLE_CLICK_THRESHOLD = 400;
      const handleMiddleMouseDown = (e: MouseEvent) => {
        if (e.button !== 1) return; // only middle button
        const now = Date.now();
        if (now - lastMiddleClickTime < DOUBLE_CLICK_THRESHOLD) {
          lastMiddleClickTime = 0;
          const cam = world.camera as OBC.OrthoPerspectiveCamera;
          cam.fit(world.meshes);
          needsRenderRef.current = true;
        } else {
          lastMiddleClickTime = now;
        }
      };
      canvasEl.addEventListener("mousedown", handleMiddleMouseDown);

      // --- Selection highlight: tint the actual element via fragments worker ---
      const HIGHLIGHT_COLOR = new THREE.Color(0xcc6600); // darker orange
      // RenderedFaces.TWO = 1 (both sides) so thin elements stay visible
      const HIGHLIGHT_MATERIAL = {
        color: HIGHLIGHT_COLOR,
        renderedFaces: 1,
        opacity: 1,
        transparent: false,
      } as const;
      // Multi-select: one set of localIds per model
      const selection = selectionRef.current;

      const updateSelectionState = () => {
        let count = 0;
        for (const ids of selection.values()) count += ids.size;
        setHasSelection(count > 0);
      };
      //console.log("[SELECT] selection system initialized");
      const clearSelection = async () => {
        for (const [model, ids] of selection) {
          try {
            await model.resetHighlight([...ids]);
          } catch (err) {
            console.warn("[SELECT] resetHighlight error:", err);
          }
        }
        selection.clear();
        updateSelectionState();
      };

      const toggleInSelection = async (model: any, id: number) => {
        const ids = selection.get(model);
        if (ids?.has(id)) {
          ids.delete(id);
          try {
            await model.resetHighlight([id]);
          } catch (err) {
            console.warn("[SELECT] resetHighlight error:", err);
          }
          if (ids.size === 0) selection.delete(model);
        } else {
          try {
            await model.highlight([id], HIGHLIGHT_MATERIAL);
          } catch (err) {
            console.error("[SELECT] highlight error:", err);
            return;
          }
          if (ids) ids.add(id);
          else selection.set(model, new Set([id]));
        }
        updateSelectionState();
      };

      let pointerDownPos: { x: number; y: number } | null = null;
      let pointerDownTime = 0;

      canvasEl.addEventListener("pointerdown", (e: PointerEvent) => {
        if (e.button !== 0) return;
        pointerDownPos = { x: e.clientX, y: e.clientY };
        pointerDownTime = Date.now();
      });

      const caster = casters.get(world);
      void caster;
      container.addEventListener("click", async (e: MouseEvent) => {
        if (clipper.enabled) return;
        if (pointerDownPos) {
          const dx = e.clientX - pointerDownPos.x;
          const dy = e.clientY - pointerDownPos.y;
          if (dx * dx + dy * dy > 16) { pointerDownPos = null; return; }
          if (Date.now() - pointerDownTime > 500) { pointerDownPos = null; return; }
        }
        pointerDownPos = null;

        // model.raycast's `mouse` is in CLIENT PIXEL coords (not NDC) —
        // screenToCast subtracts rect.left internally.
        const mousePx = new THREE.Vector2(e.clientX, e.clientY);
        const camera = (world.camera as OBC.OrthoPerspectiveCamera).three;

        // Worker-based raycast per model gives the correct localId.
        let best: any = null;
        for (const [, model] of fragments.list) {
          try {
            const r = await (model as any).raycast({
              camera,
              mouse: mousePx,
              dom: canvasEl,
            });
            if (r && (!best || r.distance < best.distance)) {
              best = r;
              best.__model = model;
            }
          } catch (err) {
            console.error("[SELECT] model.raycast error:", err);
          }
        }

        const additive = e.ctrlKey || e.metaKey;

        if (best && best.localId != null) {
          const model = best.__model;
          const id = best.localId as number;
          if (additive) {
            await toggleInSelection(model, id);
            console.log("[SELECT] toggled localId", id);
          } else {
            await clearSelection();
            await toggleInSelection(model, id);

            const guids = await best.__model.getGuidsByLocalIds([best.localId]);
            const guid = guids?.[0];
            if (!guid) return;
            const guidString = guid.toString();
            //console.log("[SELECT] clicked guid", guidString);
            onObjectClickRef.current?.(guidString);

          }
        } else if (!additive) {
          // Click in empty space without Ctrl → clear everything
          await clearSelection();
          console.log("[SELECT] cleared (no hit)");
        }

        // Move orbit pivot to the center of the selection's merged bbox.
        const controls = (world.camera as OBC.OrthoPerspectiveCamera).controls;
        if (selection.size > 0) {
          const merged = new THREE.Box3();
          for (const [model, ids] of selection) {
            try {
              const boxes: THREE.Box3[] = await model.getBoxes([...ids]);
              for (const b of boxes) merged.union(b);
            } catch { /* skip */ }
          }
          if (!merged.isEmpty()) {
            const center = new THREE.Vector3();
            merged.getCenter(center);
            controls.setTarget(center.x, center.y, center.z, true);
          }
        }

        // Force the worker to repaint affected tiles with the new highlight.
        await fragments.core.update(true);
        needsRenderRef.current = true;
        (world.renderer as OBC.SimpleRenderer).three.render(
          world.scene.three,
          (world.camera as OBC.OrthoPerspectiveCamera).three,
        );
      });

      debugLog("[FragBimViewer] OBC scene ready");
      setSceneReady(true);
    })().catch((error) => {
      console.error("[FragBimViewer] failed to initialize OBC scene", error);
      if (!cancelled) {
        setLoadStatus({
          title: "Kunne ikke starte 3D-visningen",
          detail: error instanceof Error ? error.message : "Det oppstod en ukjent feil.",
          blocking: true,
          tone: "error",
        });
      }
    });

    // Animation loop
    const tmpDir = new THREE.Vector3();
    const tmpTarget = new THREE.Vector3();
    const tmpPos = new THREE.Vector3();
    let animFrame = 0;
    let debugFrame = 0;

    const animate = () => {
      animFrame = requestAnimationFrame(animate);

      if (!ctxRef.current) return;
      const ctx = ctxRef.current;
      const cam = (ctx.world.camera as OBC.OrthoPerspectiveCamera).three as THREE.PerspectiveCamera;
      const controls = (ctx.world.camera as OBC.OrthoPerspectiveCamera).controls;

      const sync = syncCameraRef.current;
      let shouldRender = needsRenderRef.current;

      // Gizmo animation drives the camera → need full scene render
      const gizmo = gizmoRef.current;
      if (gizmo?.animating) shouldRender = true;

      if (sync) {
        controls.enabled = false;
        // Recompute model bbox center from the live scene every time the sync
        // target changes — modelCenter3DRef may be null (no fit yet) or stale.
        console.log("Syncing camera to:", sync);
        const zoomKey = sync.zoom2d != null ? sync.zoom2d.toFixed(3) : "_";
        const syncKey = sync.ifcModelCenter
          ? `${sync.viewPoint.x.toFixed(2)},${sync.viewPoint.y.toFixed(2)},${sync.ifcModelCenter.x.toFixed(2)},${sync.ifcModelCenter.y.toFixed(2)},${sync.zFloor.toFixed(2)},${zoomKey}`
          : `${sync.viewPoint.x.toFixed(2)},${sync.viewPoint.y.toFixed(2)},${sync.zFloor.toFixed(2)},${zoomKey}`;
        const isNewSync = lastSyncKeyRef.current !== syncKey;
        if (isNewSync) {
          lastSyncKeyRef.current = syncKey;
          // Compute fresh model bbox each new sync using world.meshes (same
          // source OBC uses for cam.fit, so it's known to be populated).
          const bbox = new THREE.Box3();
          for (const mesh of ctx.world.meshes) bbox.expandByObject(mesh);
          if (!bbox.isEmpty()) {
            modelCenter3DRef.current = bbox.getCenter(new THREE.Vector3());
            lastBboxRef.current = bbox.clone();
          }
        }

        // IFC → Three.js axis mapping for this viewer:
        //   IFC_X        → Three.js X
        //   IFC_Z (up)   → Three.js Y
        //   IFC_Y        → Three.js -Z   (the .frag loader applies a -90°
        //                                  rotation around X to convert from
        //                                  IFC's Z-up to Three.js's Y-up).
        const EYE_HEIGHT_M = 10;
        const targetY = sync.zFloor + EYE_HEIGHT_M;
        const modelCenter = modelCenter3DRef.current;
        const zoomFactor = sync.zoom2d;
        console.log("Sync zoom2d:", zoomFactor);
        if (modelCenter && sync.ifcModelCenter) {
          const dx = sync.viewPoint.x - sync.ifcModelCenter.x;
          const dz = -(sync.viewPoint.y - sync.ifcModelCenter.y);
          tmpTarget.set(modelCenter.x + dx, targetY, modelCenter.z + dz);
        } else {
          // Fallback (no model loaded yet) — use raw IFC coords with flip.
          tmpTarget.set(sync.viewPoint.x, targetY, -sync.viewPoint.y);
        }

        if (isNewSync) {
          const bbox = lastBboxRef.current;
          debugLog("[FragBimViewer] sync →", {
            viewPointIfc: sync.viewPoint,
            ifcModelCenter: sync.ifcModelCenter,
            zFloor: sync.zFloor,
            modelCenter3D: modelCenter
              ? { x: +modelCenter.x.toFixed(2), y: +modelCenter.y.toFixed(2), z: +modelCenter.z.toFixed(2) }
              : null,
            bbox3D: bbox && !bbox.isEmpty()
              ? {
                  min: bbox.min.toArray().map((v) => +v.toFixed(2)),
                  max: bbox.max.toArray().map((v) => +v.toFixed(2)),
                }
              : null,
            target: { x: +tmpTarget.x.toFixed(2), y: +tmpTarget.y.toFixed(2), z: +tmpTarget.z.toFixed(2) },
          });
        }

        // Place camera at a steep elevation looking at the target. A near
        // top-down view minimizes the visual impact of any small horizontal
        // mismatch between the 2D viewport center and the 3D sync target.
        // The horizontal offset is rotated around Y by the 2D viewer's plan
        // rotation so the synced 3D view is oriented the same way as the plan.
        //
        // Distance is derived from the 2D viewer's deck.gl zoom so both
        // viewports show roughly the same world-space extent:
        //   deck.gl: worldUnitsPerPixel = 2 ** -zoom
        //   perspective: visibleHeight = 2 * d * tan(fov/2)
        //   → d = (canvasH * 2 ** -zoom) / (2 * tan(fov/2))
        const FALLBACK_OFFSET = 30;
        const MIN_OFFSET = 0.5;
        const MAX_OFFSET = 400;
        const canvasH = (ctx.world.renderer as OBC.SimpleRenderer).three.domElement.clientHeight || 800;
        let cameraOffset = FALLBACK_OFFSET;
        if (sync.zoom2d != null && Number.isFinite(sync.zoom2d)) {
          const fovRad = (cam.fov * Math.PI) / 180;
          const worldHeight = canvasH * 2 ** -sync.zoom2d;
          const derived = worldHeight / (2 * Math.tan(fovRad / 2));
          cameraOffset = Math.max(MIN_OFFSET, Math.min(MAX_OFFSET, derived));
          if (isNewSync) {
            console.log("[FragBimViewer] zoom→offset", {
              zoom2d: sync.zoom2d,
              canvasH,
              fov: cam.fov,
              worldHeight: +worldHeight.toFixed(2),
              derivedOffset: +derived.toFixed(2),
              clampedOffset: +cameraOffset.toFixed(2),
            });
          }
        } else if (isNewSync) {
          console.log("[FragBimViewer] zoom2d missing, using fallback offset", FALLBACK_OFFSET);
        }
        const ELEVATION_DEG = 75;
        const elevRad = (ELEVATION_DEG * Math.PI) / 180;
        const horiz = cameraOffset * Math.cos(elevRad);
        const vert = cameraOffset * Math.sin(elevRad);
        // Default look direction (angleDeg = 0): camera behind target along -Z
        // → looks toward +Z. Rotate offset around Y by angleDeg.
        // The 2D viewer's pipeline is `flipPoint` (swap X/Y) then `rotate(angle)`,
        // which introduces a 90° offset between 2D plan rotation and Three.js
        // world rotation around Y. Compensate with +90°.
        const baseOffX = 0;
        const baseOffZ = -horiz;
        const rad = (((sync.angleDeg ?? 0) + 90) * Math.PI) / 180;
        const cosA = Math.cos(rad);
        const sinA = Math.sin(rad);
        const offX = cosA * baseOffX - sinA * baseOffZ;
        const offZ = sinA * baseOffX + cosA * baseOffZ;
        tmpPos.set(
          tmpTarget.x + offX,
          tmpTarget.y + vert,
          tmpTarget.z + offZ,
        );
        controls.setLookAt(
          tmpPos.x, tmpPos.y, tmpPos.z,
          tmpTarget.x, tmpTarget.y, tmpTarget.z,
          false,
        );
        if (gizmo) gizmo.target.copy(tmpTarget);
        shouldRender = true;
      } else {
        // Sync just ended — clear last sync key so next activation logs again.
        lastSyncKeyRef.current = "";
        controls.enabled = true;
        cam.getWorldDirection(tmpDir);
        controls.getTarget(tmpTarget);
        //console.log("Camera dir:", tmpDir, "Target:", tmpTarget);
        // Keep gizmo target in sync with orbit target
        if (gizmo) gizmo.target.copy(tmpTarget);
        const dist = cam.position.distanceTo(tmpTarget);
        if (shouldRender) {
          onCameraChangeRef.current?.([tmpDir.x, tmpDir.y, tmpDir.z], dist);
        }
      }

      if (shouldRender) {
        needsRenderRef.current = false;
      }

      // Debug overlay — throttled
      if (debugOverlayRef.current && shouldRender) {
        debugFrame += 1;
        if (debugFrame % 6 === 0) {
          cam.getWorldDirection(tmpDir);
          const sync2 = syncCameraRef.current;
          setDebugInfo({
            camPos: cam.position.clone(),
            camDir: tmpDir.clone(),
            target: controls.getTarget(new THREE.Vector3()),
            syncTarget: sync2
              ? new THREE.Vector3(sync2.viewPoint.x, sync2.zFloor + 1.5, -sync2.viewPoint.y)
              : null,
          });
        }
      }
    };
    animate();

    return () => {
      debugLog("[FragBimViewer] OBC scene teardown");
      cancelled = true;
      cancelAnimationFrame(animFrame);
      onLoadedModelIdsChangeRef.current?.([]);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      gizmoRef.current?.dispose();
      gizmoRef.current = null;
      if (gizmoRendererRef.current) {
        gizmoRendererRef.current.dispose();
        gizmoRendererRef.current.domElement.remove();
        gizmoRendererRef.current = null;
      }
      components.dispose();
      ctxRef.current = null;
      setSceneReady(false);
    };
  }, []);

  // --- Orbit gizmo visibility ---
  useEffect(() => {
    const gizmo = gizmoRef.current;
    if (!gizmo) return;
    gizmo.enabled = showOrbitGizmo;
  }, [showOrbitGizmo, sceneReady]);

  // --- Grid visibility ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.grid.visible = gridVisible;
    needsRenderRef.current = true;
  }, [gridVisible, sceneReady]);

  useEffect(() => {
    if (sceneReady && !models?.length) setLoadStatus(null);
  }, [sceneReady, models?.length]);

  // --- Clipper toggle ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.clipper.enabled = clipperActive;

    const canvas = (ctx.world.renderer as OBC.SimpleRenderer).three.domElement;

    const handleDblClick = () => {
      if (ctx.clipper.enabled) {
        ctx.clipper.create(ctx.world);
        needsRenderRef.current = true;
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!ctx.clipper.enabled) return;
      if (event.key === "Delete" || event.key === "Backspace") {
        ctx.clipper.delete(ctx.world);
        needsRenderRef.current = true;
      }
      if (event.key === "Escape") {
        onClipperActiveChange?.(false);
      }
    };

    if (clipperActive) {
      canvas.addEventListener("dblclick", handleDblClick);
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      canvas.removeEventListener("dblclick", handleDblClick);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [clipperActive, sceneReady, onClipperActiveChange]);

  // --- Delete all clip planes ---
  useEffect(() => {
    if (!deleteAllClipPlanesSignal) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.clipper.deleteAll();
    needsRenderRef.current = true;
  }, [deleteAllClipPlanesSignal]);

  // --- Load models ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!sceneReady || !ctx || !models?.length) return;

    const { fragments, loadedModels, world } = ctx;
    const core = fragments.core;
    const requestedModelIds = new Set(models.map(getModelInputId));
    desiredModelIdsRef.current = requestedModelIds;
    let cancelled = false;
    const effectLoadingModelIds = new Set<string>();

    (async () => {
      debugLog("[FragBimViewer] models effect running, count:", models.length);
      let changed = false;

      // Dispose models no longer in the list
      for (const [id, entry] of loadedModels) {
        if (!requestedModelIds.has(id)) {
          world.scene.three.remove(entry.model.object);
          await core.disposeModel(id);
          loadedModels.delete(id);
          changed = true;
        }
      }
      if (changed) notifyRetainedModelIds(loadedModels);
      if (loadedModels.size === 0) hasFitCameraRef.current = false;
      const shouldFitCamera = !hasFitCameraRef.current && loadedModels.size === 0;
      const hasLoadedModelsAtStart = loadedModels.size > 0;
      const inputsToLoad = models.filter((input) => {
        const modelId = getModelInputId(input);
        return !loadedModels.has(modelId) && !loadingModelIdsRef.current.has(modelId);
      });

      if (inputsToLoad.length) {
        setLoadStatusThrottled({
          title: hasLoadedModelsAtStart ? "Laster nye 3D-filer" : "Laster 3D-visning",
          detail: `${inputsToLoad.length} frag-fil${inputsToLoad.length === 1 ? "" : "er"} skal bygges opp...`,
          blocking: !hasLoadedModelsAtStart,
        }, true);
      }

      for (let i = 0; i < inputsToLoad.length; i++) {
        const input = inputsToLoad[i];
        const modelId = getModelInputId(input);
        if (loadedModels.has(modelId) || loadingModelIdsRef.current.has(modelId) || cancelled) continue;

        loadingModelIdsRef.current.add(modelId);
        effectLoadingModelIds.add(modelId);
        notifyRetainedModelIds(loadedModels);
        setLoadStatusThrottled({
          title: `Laster 3D-fil ${i + 1}/${inputsToLoad.length}`,
          detail: input.name,
          blocking: !hasLoadedModelsAtStart,
        }, true);
        await yieldToBrowser();
        if (cancelled) {
          loadingModelIdsRef.current.delete(modelId);
          continue;
        }

        let model: Awaited<ReturnType<typeof core.load>>;
        try {
          model = await core.load(input.buffer, { modelId });
        } catch (error) {
          loadingModelIdsRef.current.delete(modelId);
          effectLoadingModelIds.delete(modelId);
          notifyRetainedModelIds(loadedModels);
          throw error;
        }
        loadingModelIdsRef.current.delete(modelId);
        effectLoadingModelIds.delete(modelId);
        if (!desiredModelIdsRef.current.has(modelId)) {
          await core.disposeModel(modelId);
          notifyRetainedModelIds(loadedModels);
          continue;
        }

        loadedModels.set(modelId, { model, name: input.name });
        changed = true;
        needsRenderRef.current = true;
        notifyRetainedModelIds(loadedModels);
      }

      if (changed && ctxRef.current === ctx) {
        notifyRetainedModelIds(loadedModels);
        debugLog("[FragBimViewer] all models loaded, calling fragments.core.update");
        const currentFilter = filterRef.current;
        if (currentFilter !== undefined) {
          setLoadStatusThrottled({
            title: "Filtrerer 3D-visning",
            detail: "Skjuler valgte IFC-typer...",
            blocking: !hasLoadedModelsAtStart,
          }, true);
          await applyVisibilityFilterToModels(loadedModels, currentFilter);
        }
        setLoadStatusThrottled({
          title: "Bygger 3D-visning",
          detail: "Oppretter synlig geometri og oppdaterer klippingen...",
          blocking: !hasLoadedModelsAtStart,
        }, true);
        await core.update(true);
        debugLog("[FragBimViewer] fragments.core.update done");
        setLoadedModelVersion((v) => v + 1);
        needsRenderRef.current = true;

        if (shouldFitCamera) {
          setLoadStatusThrottled({
            title: "Rendrer 3D-visning",
            detail: "Posisjonerer kameraet...",
            blocking: true,
          }, true);
          const cam = world.camera as OBC.OrthoPerspectiveCamera;
          await cam.fit(world.meshes);
          hasFitCameraRef.current = true;
          // Store model center for sync offset calculations
          const bbox = new THREE.Box3();
          for (const mesh of world.meshes) bbox.expandByObject(mesh);
          if (!bbox.isEmpty()) {
            modelCenter3DRef.current = bbox.getCenter(new THREE.Vector3());
          }
        }
        window.setTimeout(() => {
          if (!cancelled && ctxRef.current === ctx) setLoadStatus(null);
        }, 150);
      } else if (!changed && !loadingModelIdsRef.current.size && ctxRef.current === ctx) {
        setLoadStatus(null);
      }
    })().catch((error) => {
      console.error("[FragBimViewer] failed to load fragments", error);
      if (!cancelled && ctxRef.current === ctx) {
        setLoadStatus({
          title: "Kunne ikke laste 3D-visningen",
          detail: error instanceof Error ? error.message : "Det oppstod en ukjent feil.",
          blocking: true,
          tone: "error",
        });
      }
    });

    return () => {
      cancelled = true;
      for (const modelId of effectLoadingModelIds) {
        core.abort(modelId);
        loadingModelIdsRef.current.delete(modelId);
      }
      effectLoadingModelIds.clear();
      if (ctxRef.current === ctx) notifyRetainedModelIds(ctx.loadedModels);
    };
  }, [sceneReady, modelsKey]);

  // --- Filter ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (filter === undefined) return;
    const { fragments, loadedModels } = ctx;
    if (loadedModels.size === 0) return;
    let cancelled = false;

    (async () => {
      await applyVisibilityFilterToModels(loadedModels, filter);
      if (cancelled) return;
      await fragments.core.update(true);
      needsRenderRef.current = true;
    })().catch((error) => {
      console.error("[FragBimViewer] failed to apply filter", error);
    });

    return () => { cancelled = true; };
  }, [filter]);

  // --- Color rules ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const { fragments, loadedModels } = ctx;

    (async () => {
      await applyColorRulesToModels(loadedModels, colorRules);
      await fragments.core.update(true);
      needsRenderRef.current = true;
    })();
  }, [colorRules]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }} />
      <div
        ref={gizmoContainerRef}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 120,
          height: 120,
          pointerEvents: "auto",
          zIndex: 10,
        }}
      />
      <LoadStatusOverlay status={loadStatus} />
      <DebugOverlay enabled={debugOverlay} debugInfo={debugInfo} />
      {(hasSelection || hasHiddenObjects) && (
        <div
          style={{
            position: "absolute",
            bottom: 14,
            right: 14,
            zIndex: 25,
            display: "flex",
            gap: 4,
            background: "white",
            borderRadius: 8,
            boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
            padding: 4,
          }}
        >
          {hasSelection && (
            <>
              <button
                type="button"
                title="Skjul markerte objekter"
                style={{
                  height: 30,
                  border: 0,
                  borderRadius: 6,
                  padding: "0 10px",
                  background: "transparent",
                  color: "#495057",
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
                onClick={async () => {
                  const ctx = ctxRef.current;
                  if (!ctx) return;
                  const selection = selectionRef.current;
                  for (const [model, ids] of selection) {
                    await model.setVisible([...ids], false);
                  }
                  for (const [model, ids] of selection) {
                    await model.resetHighlight([...ids]);
                  }
                  selection.clear();
                  setHasSelection(false);
                  setHasHiddenObjects(true);
                  await ctx.fragments.core.update(true);
                  needsRenderRef.current = true;
                }}
              >
                <EyeOffIcon />
                Skjul
              </button>
              <button
                type="button"
                title="Isoler markerte objekter (skjul alt annet)"
                style={{
                  height: 30,
                  border: 0,
                  borderRadius: 6,
                  padding: "0 10px",
                  background: "transparent",
                  color: "#495057",
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
                onClick={async () => {
                  const ctx = ctxRef.current;
                  if (!ctx) return;
                  const selection = selectionRef.current;
                  for (const [, model] of ctx.fragments.list) {
                    await model.setVisible(undefined, false);
                  }
                  for (const [model, ids] of selection) {
                    await model.setVisible([...ids], true);
                  }
                  setHasHiddenObjects(true);
                  await ctx.fragments.core.update(true);
                  needsRenderRef.current = true;
                }}
              >
                <IsolateIcon />
                Isoler
              </button>
            </>
          )}
          {hasHiddenObjects && (
            <button
              type="button"
              title="Vis alle objekter"
              style={{
                height: 30,
                border: 0,
                borderRadius: 6,
                padding: "0 10px",
                background: "transparent",
                color: "#495057",
                cursor: "pointer",
                font: "inherit",
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
              onClick={async () => {
                const ctx = ctxRef.current;
                if (!ctx) return;
                for (const [, model] of ctx.fragments.list) {
                  await model.resetVisible();
                }
                setHasHiddenObjects(false);
                await ctx.fragments.core.update(true);
                needsRenderRef.current = true;
              }}
            >
              <EyeIcon />
              Vis alle
            </button>
          )}
        </div>
      )}
      {clipperActive && (
        <div
          style={{
            position: "absolute",
            bottom: 14,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            pointerEvents: "none",
            background: "rgba(15, 23, 42, 0.72)",
            color: "white",
            fontSize: 12,
            lineHeight: 1.4,
            borderRadius: 6,
            padding: "6px 14px",
            whiteSpace: "nowrap",
          }}
        >
          Dobbeltklikk på en flate for å plassere et klipp · Dra pilen for å flytte klippet · Delete for å fjerne · ESC for å avslutte
        </div>
      )}
    </div>
  );
};

export default React.memo(FragBimViewer);
